/** A line that is nothing but a closing ``` fence (up to three spaces of indent). */
const CLOSE_FENCE_REGEX = /(?:^|\n)[ \t]{0,3}```[ \t]*(?=\n|$)/g;

/** Info strings that mark a fence as a markdown wrapper rather than a code block. */
const WRAPPER_LANGUAGES = ['markdown', 'md'];

/**
 * Strips a wrapping code fence from content where the LLM has wrapped
 * its entire markdown response in a single ``` block.
 *
 * This commonly happens when LLMs output markdown content wrapped in
 * ```markdown ... ``` or just ``` ... ``` fences, causing the markdown
 * parser to render everything as a code block instead of formatted markdown.
 *
 * The function only strips a bare ``` fence or an explicit ```markdown /
 * ```md hint, and only when the inner content contains markdown formatting
 * indicators (headings, bold, lists, horizontal rules), to avoid stripping
 * legitimate code blocks.
 *
 * A fence only counts as *wrapping* when it spans the whole response: either
 * it is never closed (the LLM forgot the closing fence), or its closing fence
 * is the last thing in the message. A fence that closes mid-message delimits
 * one block inside the answer — e.g. a quoted system prompt followed by a
 * follow-up question — and stripping its opening fence would shift every
 * later fence by one, dropping the block's content into the prose and pulling
 * the trailing prose into a code block.
 */
export function stripWrappingCodeFence(content: string): string {
  if (!content) return content;

  const trimmed = content.trim();

  // Match opening code fence at the very start: ``` or ```markdown or ```md etc.
  const openFenceMatch = trimmed.match(/^```(\w*)\s*\n/);
  if (!openFenceMatch) return content;

  // Only a bare fence or an explicit markdown hint can be a *wrapper*. Any other
  // info string (```text, ```json, ```html, ```echarts) is the model deliberately
  // asking for a code block — most often a generated system prompt the user wants
  // to read and copy verbatim — and must survive untouched.
  const language = openFenceMatch[1].toLowerCase();
  if (language && !WRAPPER_LANGUAGES.includes(language)) return content;

  let body = trimmed.slice(openFenceMatch[0].length);

  // Find the last closing fence (a line consisting only of ```), and bail out
  // when anything but whitespace follows it — that fence closes a block inside
  // the answer, not the answer itself.
  // Index instead of `.at(-1)`: this file is now also bundled into the public
  // chat frontend, whose Angular 15 browser matrix still includes Safari 14 —
  // `Array.prototype.at` only landed in Safari 15.4 and is not downlevelled.
  const closeFences = [...body.matchAll(CLOSE_FENCE_REGEX)];
  const lastCloseFence = closeFences[closeFences.length - 1];

  if (lastCloseFence?.index !== undefined) {
    const afterCloseFence = body.slice(lastCloseFence.index + lastCloseFence[0].length);
    if (afterCloseFence.trim()) return content;
    body = body.slice(0, lastCloseFence.index);
  }

  // Only strip if the body looks like markdown (contains formatting indicators).
  // This prevents stripping legitimate fenced code blocks.
  const looksLikeMarkdown = /(?:^|\n)#{1,6}\s|(?:^|\n)\*\*[^*]+\*\*|(?:^|\n)[-*]\s+\S|(?:^|\n)---\s*(?:\n|$)|(?:^|\n)\d+\.\s+\S/.test(body);

  if (looksLikeMarkdown) {
    return body.trim();
  }

  return content;
}
